import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import {
  CloseCircleOutline,
  ExclamationCircleOutline,
  useToast,
} from '@aslanpay/portal-common-lib';
import useAuthContext from 'app/hooks/useAuthContext';

import useDeleteCompany from 'app/hooks/useDeleteCompany';
import useSignOut from 'app/hooks/useSignOut';

import { DeleteCompanyModalProps } from './types';

function DeleteCompanyModal({ id, isOpen, onClose, onDelete }: DeleteCompanyModalProps) {
  const { user } = useAuthContext();

  const toast = useToast();

  const queryClient = useQueryClient();

  const isUserCompany = user?.companyId === id;

  const { mutate: signOut } = useSignOut();

  const { mutate: deleteCompany, isLoading: isDeletingCompany } = useDeleteCompany({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Company deleted',
        description: 'The company was succesfully deleted!',
      });

      if (isUserCompany) {
        // If we are deleting the user company we will also delete the user, so, the sign out is needed.
        signOut();
      } else {
        queryClient.invalidateQueries({ queryKey: ['fetchCompaniesPage'] });

        onDelete();
        onClose();
      }
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: `Sorry, the company was not deleted. Please try again.`,
      });

      onClose();
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <ExclamationCircleOutline color="system.error" boxSize="24px" />
            <Text as="h6">Delete Company?</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close delete company"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Text color="brand.mutedPurple" textStyle="body">
            This will delete the company and all the data that belongs to it (admins, employees, pay
            calendars, etc...) and you will not be able to recover it after confirm.
          </Text>

          {isUserCompany && (
            <Text mt="10px" color="brand.mutedPurple" textStyle="body">
              <Text as="span" textStyle="h6Medium">
                Warning:
              </Text>{' '}
              Your account will also be deleted and you will lose access to the Aslan Web Portal.
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Spacer />
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" isLoading={isDeletingCompany} onClick={() => deleteCompany({ id })}>
            Yes, delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default DeleteCompanyModal;
